import React, { HTMLAttributes, Ref, forwardRef } from 'react';
import { classNames } from 'primereact/utils';

import './ProgressBar.scss';

export type ProgressBarVariant = 'primary';

export type ProgressBarProps = Pick<HTMLAttributes<HTMLDivElement>, 'className' | 'style'> & {
	current: number;
	max: number;
	variant?: ProgressBarVariant;
};

const getProgressPercent = (current: number, max: number): number => {
	if (max <= 0) {
		return 0;
	}

	if (current >= max) {
		return 100;
	}

	const ratio = (current / max) * 100;
	return Math.max(0, Math.min(ratio, 100));
};

export const ProgressBar = forwardRef(
	({ current, max, variant = 'primary', className, style }: ProgressBarProps, ref: Ref<HTMLDivElement>) => {
		const progressPercent = getProgressPercent(current, max);
		const normalizedCurrent = Math.max(0, max > 0 ? Math.min(current, max) : current);

		return (
			<div
				ref={ref}
				className={classNames('progress-bar', `progress-bar-variant-${variant}`, className)}
				style={style}
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={max > 0 ? max : 0}
				aria-valuenow={normalizedCurrent}
			>
				<div
					className={classNames('progress-bar-fill', `progress-bar-fill-variant-${variant}`)}
					style={{ width: `${progressPercent}%` }}
				/>
			</div>
		);
	}
);
